import { useState } from "react";
import { motion } from "framer-motion";
import { HeartHandshake, Shield, Sunrise, Heart, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { JOURNEYS } from "@/data/journeys";

const ICONS = { HeartHandshake, Shield, Sunrise, Heart };

export default function Journeys({ onAsk }) {
  const [active, setActive] = useState(null);

  return (
    <section data-testid="journeys" className="mx-auto max-w-4xl px-6 pb-8 pt-16">
      <p className="mb-2 text-center font-sans text-xs uppercase tracking-[0.25em] text-primary">Guided Journeys</p>
      <h2 className="mb-8 text-center font-serif text-3xl tracking-tight text-foreground sm:text-4xl">
        Explore a theme, one question at a time
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {JOURNEYS.map((j, i) => {
          const Icon = ICONS[j.icon] || Heart;
          return (
            <motion.button
              key={j.id}
              data-testid="journey-card"
              onClick={() => setActive(j)}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              whileHover={{ y: -6 }}
              className="group flex flex-col items-start rounded-lg border border-border bg-card p-6 text-left shadow-sm transition-shadow hover:shadow-lg"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/50 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-serif text-2xl tracking-tight text-foreground">{j.title}</h3>
              <p className="mt-1 font-sans text-sm text-muted-foreground">{j.subtitle}</p>
              <span className="mt-3 font-sans text-xs uppercase tracking-[0.15em] text-primary">
                {j.questions.length} questions
              </span>
            </motion.button>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="journey-dialog" className="max-w-lg bg-background">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">{active?.title}</DialogTitle>
            <DialogDescription className="font-sans">{active?.subtitle}</DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {active?.questions.map((q, idx) => (
              <button
                key={q}
                data-testid="journey-question"
                onClick={() => { setActive(null); onAsk(q); }}
                className="group flex w-full items-center gap-4 rounded-md border border-border bg-card p-4 text-left transition-colors hover:border-primary"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted font-serif text-lg text-primary">
                  {idx + 1}
                </span>
                <span className="flex-1 font-body text-[1.02rem] text-foreground/90">{q}</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
